/** Format a relative time string with "ago" suffix (e.g. "5m ago", "3h ago", "2 May") */
export function relativeTimeAgo(
  iso: string,
  t: (key: string) => string,
): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t('relativeTime.justNow');
  if (mins < 60) return `${mins}${t('relativeTime.minsAgo')}`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}${t('relativeTime.hoursAgo')}`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}${t('relativeTime.daysAgo')}`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}${t('relativeTime.weeksAgo')}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}${t('relativeTime.monthsAgo')}`;
  const years = Math.floor(days / 365);
  return `${years}${t('relativeTime.yearsAgo')}`;
}

/** Format a relative time string from an ISO timestamp (e.g. "5m", "3h", "2d") */
export function relativeTime(iso: string, t: (key: string) => string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t('relativeTime.justNow');
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/** Convert a date string to a full ISO 8601 string; returns undefined if invalid */
export function toIso(val: string): string | undefined {
  if (!val) return undefined;
  try {
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  } catch {
    return undefined;
  }
}

/** Format a byte count as a human-readable file size string */
export function humanFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

/** Format an ISO date string (YYYY-MM-DD or full ISO) for display: "Jan 3, 2025" */
export function formatDateDisplay(iso: string): string {
  if (!iso) return '';
  // Fixed calendar dates (audit/certificate dates) arrive as date-only strings or
  // full UTC instants. Parse date-only as UTC midnight and render in UTC so the
  // day never shifts by the viewer's timezone (mirrors `formatDate`).
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso)
    ? new Date(`${iso}T00:00:00.000Z`)
    : new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Trim an ISO datetime to YYYY-MM-DD for use in date inputs */
export function toDateInput(iso?: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

/**
 * Derive a stable CSS color variable from an entity id using the AVATAR_PALETTE.
 * Used for avatar background colours.
 */
export function avatarColor(
  id: string | null | undefined,
  palette: string[],
): string {
  if (!id) return palette[0] ?? 'var(--entity-1)';
  let hash = 0;
  for (let i = 0; i < id.length; i++)
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length] ?? 'var(--entity-1)';
}

/** Extract initials from a full name (up to 2 words) */
export function initials(name: string | null | undefined): string {
  if (!name) return '';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('');
}

// Built once, not per call. `toLocaleString` with options constructs a new
// `Intl.DateTimeFormat` every time, and these run for every date cell on every
// table render — on the audits list that was ~14% of all scripting time while
// scrolling (DELTA-2693). Same locale and options, so the output is identical.
const MONTH_SHORT_UTC = new Intl.DateTimeFormat('en', {
  month: 'short',
  timeZone: 'UTC',
});
const MONTH_SHORT_LOCAL = new Intl.DateTimeFormat('en', { month: 'short' });

/**
 * Format an ISO date string to "day Month year" using UTC, e.g. "3 Jan 2025".
 * Used for table cell date display.
 */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = d.getUTCDate();
  const month = MONTH_SHORT_UTC.format(d);
  const year = d.getUTCFullYear();
  return `${day} ${month} ${year}`;
}

/**
 * Format an ISO datetime string to "day Month · HH:MM" using local time.
 * Used for table cell datetime display.
 */
export function formatDatetime(iso: string): string {
  const d = new Date(iso);
  const day = d.getDate();
  const month = MONTH_SHORT_LOCAL.format(d);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} · ${hh}:${mm}`;
}

/**
 * Format an ISO datetime string to "day Month year, HH:MM" in local time,
 * e.g. "10 Sep 2026, 14:30". The date-with-time counterpart of `formatDate`,
 * for messages and other events where the time matters.
 */
export function formatDateWithTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const month = MONTH_SHORT_LOCAL.format(d);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${month} ${d.getFullYear()}, ${hh}:${mm}`;
}

/**
 * Format an ISO datetime as "dd.MM.yyyy HH:mm" in local time, e.g.
 * "14.07.2026 11:28". Matches the CAPA comment timestamp format from v1
 * (date-fns `format(date, 'dd.MM.yyyy HH:mm')`).
 */
export function formatCommentTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
}

/**
 * Convert a supply-chain tier value (string or array) to a human-readable label.
 * Returns null when the input is empty or unrecognisable.
 */
export function formatTier(tier: unknown): string | null {
  if (!tier) return null;
  const raw = Array.isArray(tier)
    ? (tier as { id?: string }[])[0]?.id
    : String(tier);
  if (!raw) return null;
  const num = raw.replace(/^TIER_?/i, '');
  return num ? `Tier ${num}` : null;
}

/**
 * Groups a number for display — `5000000` → `5,000,000`. Long unseparated
 * figures (purchase volumes, revenues, capacities) are unreadable without it.
 * Returns an empty string for null/NaN so callers can render their own empty
 * state.
 */
export function formatNumberGrouped(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '';
  return value.toLocaleString('en-US', { maximumFractionDigits: 20 });
}
